<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import Card from 'primevue/card';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import UserAvatar from '@/components/UserAvatar.vue';
import type { ProfileWallPost } from '@/types/api';
import { createMemberWallPost, fetchMemberWallPosts } from '@/lib/apiClient';
import { useAuth } from '@/composables/useAuth';

const props = defineProps<{
  wallUserId: number;
  wallOwnerNickname: string;
}>();

const toast = useToast();
const { token, user } = useAuth();

const loading = ref(true);
const submitting = ref(false);
const posts = ref<ProfileWallPost[]>([]);
const message = ref('');

async function loadPosts() {
  if (!token.value) return;
  posts.value = (await fetchMemberWallPosts(token.value, props.wallUserId)).posts;
}

onMounted(async () => {
  try {
    await loadPosts();
  } finally {
    loading.value = false;
  }
});

async function submitPost() {
  if (!token.value || !message.value.trim()) return;
  submitting.value = true;

  try {
    await createMemberWallPost(token.value, props.wallUserId, { message: message.value });
    message.value = '';
    await loadPosts();
    toast.add({ severity: 'success', summary: 'Запись добавлена', life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось отправить',
      life: 3000,
    });
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <Card class="wall">
    <template #title>Стена {{ wallOwnerNickname }}</template>
    <template #subtitle>
      {{
        user?.id === wallUserId
          ? 'Вы и другие гости можете оставлять записи здесь'
          : 'Напишите что-нибудь на стене этого пользователя'
      }}
    </template>
    <template #content>
      <label class="wall__compose">
        <Textarea
          v-model="message"
          rows="2"
          auto-resize
          :placeholder="
            user?.id === wallUserId
              ? 'Запись на вашей стене...'
              : `Запись для ${wallOwnerNickname}...`
          "
          fluid
        />
        <Button
          label="Опубликовать"
          icon="pi pi-send"
          size="small"
          :loading="submitting"
          :disabled="!message.trim()"
          @click="submitPost"
        />
      </label>

      <ProgressSpinner v-if="loading" />

      <div v-else-if="posts.length === 0" class="wall__empty">
        Стена пока пуста — будьте первым!
      </div>

      <div v-else class="wall__feed">
        <article v-for="post in posts" :key="post.id" class="wall__post">
          <UserAvatar :nickname="post.nickname" :avatar="post.avatar" />
          <div class="wall__body">
            <div class="wall__meta">
              <strong>{{ post.nickname }}</strong>
              <small>{{ new Date(post.createdAt).toLocaleString('ru-RU') }}</small>
            </div>
            <p v-if="post.message">{{ post.message }}</p>
            <img v-if="post.drawing" :src="post.drawing" alt="Рисунок" class="wall__drawing" />
          </div>
        </article>
      </div>
    </template>
  </Card>
</template>

<style scoped>
.wall__compose {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin-bottom: 1rem;
}

.wall__empty {
  color: #9d174d;
  text-align: center;
  padding: 1rem 0;
}

.wall__feed {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.wall__post {
  display: flex;
  gap: 0.75rem;
  padding: 0.85rem;
  border-radius: 1rem;
  background: #fff7fb;
  border: 1px solid #fbcfe8;
}

.wall__body {
  flex: 1;
  min-width: 0;
}

.wall__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.wall__meta strong {
  color: #831843;
}

.wall__meta small {
  color: #be185d;
}

.wall__body p {
  margin: 0;
  color: #9d174d;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
}

.wall__drawing {
  margin-top: 0.5rem;
  max-width: 100%;
  border-radius: 0.75rem;
}
</style>
